"use client"

import { motion } from 'framer-motion'
import Link from 'next/link'
import { ArrowLeft, Cookie, Settings, Shield, MousePointerClick, BarChart3, Globe,CheckCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Footer } from '@/components/Footer'
import { contactInfo } from "@/data/contact"
export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-[#030818] text-white">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03] grid-pattern-subtle" />
      </div>
    
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-black text-white py-20 border-b border-slate-800/50">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex items-center mb-8">
            <Link href="/">
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-white/5 border border-slate-700/50 hover:border-slate-600 backdrop-blur-sm transition-all duration-300"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

           <motion.div
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.8 }}
             className="text-center max-w-3xl mx-auto px-4"
           >
             <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-[#00dd98]/10 border border-[#00dd98]/25 rounded-2xl mb-4 sm:mb-6 shadow-lg shadow-[#00dd98]/10">
               <Cookie className="h-8 w-8 sm:h-10 sm:w-10 text-[#00dd98]" />
             </div>
             <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
               Cookie{" "}
               <span className="bg-gradient-to-r from-[#00dd98] via-[#17b88c] to-[#00dd98] bg-clip-text text-transparent">
                 Policy
               </span>
             </h1>
             <div className="w-16 sm:w-24 h-1 bg-[#00dd98] mx-auto mb-4 sm:mb-6 rounded-full shadow-lg shadow-[#00dd98]/30" />
             <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-3 sm:mb-4">
               Learn how we use cookies and similar technologies to enhance your browsing experience.
             </p>
             <p className="text-sm sm:text-base text-slate-400">
               <strong>Last Updated:</strong> October 27, 2025
             </p>
           </motion.div>
        </div>
      </div>
      
       <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-16 max-w-4xl relative z-10">
        
        <SectionCard 
          icon={<Info className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Introduction"
          content={
            <div>
              <p className="text-slate-300 mb-4 leading-relaxed">
                This Cookie Policy explains how ORAPEX (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) uses cookies and similar 
                technologies when you visit our website orapx.com. This policy explains what these technologies 
                are, why we use them, and your rights to control our use of them.
              </p>
              <p className="text-slate-300 leading-relaxed">
                By continuing to use our website, you consent to our use of cookies and similar technologies 
                as described in this policy. If you do not agree with our use of cookies, you should adjust 
                your browser settings or stop using our website.
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<Info className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="What Are Cookies?"
          content={
            <div>
              <p className="text-slate-300 mb-4 leading-relaxed">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) 
                when you visit a website. They are widely used to make websites work more efficiently and 
                to provide information to website owners.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Cookies contain information that is transferred to your device&apos;s hard drive. They help us 
                recognize you when you return to our website and remember your preferences and settings.
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<MousePointerClick className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Types of Cookies We Use"
          content={
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-[#00dd98]" />
                  Essential Cookies
                </h3>
                <p className="text-slate-300 mb-2">These cookies are necessary for the website to function properly:</p>
                <ul className="list-disc pl-6 space-y-1 text-slate-300">
                  <li>Session management</li>
                  <li>Security and authentication</li>
                  <li>User interface preferences</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-2 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-[#00dd98]" />
                  Analytics Cookies
                </h3>
                <p className="text-slate-300 mb-2">We use Google Analytics to understand website usage:</p>
                <ul className="list-disc pl-6 space-y-1 text-slate-300">
                  <li>Page views and user behavior</li>
                  <li>Traffic sources and popular content</li>
                  <li>Device and browser information</li>
                </ul>
              </div>
            </div>
          }
        />

        <SectionCard 
          icon={<Shield className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Third-Party Services"
          content={
            <div>
              <p className="text-slate-300 mb-4 leading-relaxed">
                We use Google Analytics to analyze website traffic. Google Analytics uses cookies to collect 
                information about how visitors use our website.
              </p>
              <p className="text-slate-300 leading-relaxed">
                You can opt out of Google Analytics by visiting: 
                <a href="https://tools.google.com/dlpage/gaoptout" className="text-[#00dd98] hover:underline decoration-[#00dd98]/50 ml-1" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out
                </a>
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<Settings className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Managing Cookies"
          content={
            <div>
              <p className="text-slate-300 mb-4 leading-relaxed">
                You can control cookies through your browser settings:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-300 mb-4">
                <li>Block all cookies</li>
                <li>Allow only essential cookies</li>
                <li>Delete existing cookies</li>
                <li>Set preferences for specific websites</li>
              </ul>
              <p className="text-slate-300 leading-relaxed">
                Note: Disabling cookies may affect website functionality and user experience.
              </p>
            </div>
          }
        />

        <SectionCard 
          icon={<Shield className="h-6 w-6 text-[#00dd98]" />}
          iconBg="bg-[#00dd98]/10 border border-[#00dd98]/20"
          title="Updates to This Cookie Policy"
          content={
            <div>
              <p className="text-slate-300 mb-4 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or 
                applicable laws. We will notify you of any significant changes by posting the updated policy 
                on our website and updating the &ldquo;Last Updated&rdquo; date.
              </p>
              <p className="text-slate-300 leading-relaxed">
                We encourage you to review this Cookie Policy periodically to stay informed about our use of 
                cookies and related technologies.
              </p>
            </div>
          }
        />

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden bg-slate-900/30 rounded-xl sm:rounded-2xl shadow-lg shadow-black/20 p-4 sm:p-6 lg:p-8 text-white mb-6 sm:mb-8 border border-slate-800/50 backdrop-blur-sm"
        >
          {/* soft glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#00dd98]/10 blur-3xl" />
          {/* accent line */}
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#00dd98]/80 via-[#00dd98]/30 to-transparent" />

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00dd98]/10 border border-[#00dd98]/25 flex items-center justify-center text-[#00dd98] shrink-0">
              <Globe className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold mb-2">Contact Information</h2>
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                If you have any questions about this Cookie Policy or our use of cookies, please contact us:
              </p>
            </div>
          </div>

          <div className="mt-5 bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
            <p className="mb-2 text-sm sm:text-base">
              <strong>Email:</strong> {contactInfo.email}
            </p>
            <p className="mb-2 text-sm sm:text-base">
              <strong>Website:</strong> www.orapex.com
            </p>
            <p className="text-sm sm:text-base">
              <strong>Address:</strong> ORAPEX Solutions<br />
              United States, Saudi Arabia, United Arab Emirates, Jordan, Oman
            </p>
          </div>
        </motion.div>

       
      </div>

      <Footer />
    </div>
  )
}

interface SectionCardProps {
  icon: React.ReactNode
  iconBg: string
  title: string
  content: React.ReactNode
}

function SectionCard({ icon, iconBg, title, content }: SectionCardProps) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-slate-900/30 rounded-xl sm:rounded-2xl shadow-lg shadow-black/20 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-slate-800/50 backdrop-blur-sm hover:bg-slate-900/40 transition-colors duration-300"
    >
      <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
        <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">{title}</h2>
          <div className="text-sm sm:text-base">{content}</div>
        </div>
      </div>
    </motion.div>
  )
}
